import React from 'react'
import { Field } from 'redux-form'

import classNames from './ImageSearchInput.css'
import Icon from '../Icon'
import AutoComplete from './AutoComplete'
import RatingPicker from './RatingPicker'
import DatePicker from './DatePicker'
import AreaPicker from './AreaPicker'
import CameraPicker from './CameraPicker'
import LibraryPicker from './LibraryPicker'
import AdvancedForm from './AdvancedForm'
import LimitForm from './LimitForm'
import DynamicLinkForm from './DynamicLinkForm'

import { DEFAULT_OPERATOR } from '../../redux/query'

export default function ImageSearchInput ({ value, onChange, disabled, options, addTerm, updateTerm, updateRating, removeTerm, query, activeQuery, focusInput, libraries, userHasPerm, dynlinks, addNewQuery, autoFocus, setRootProperty, clearEditingState, updateTargetDate }) {
  const onChangeText = e => onChange({
    ...value,
    text: e.target.value,
    dropdown: false,
    option: null
  })
  const onClickText = e => onChange({
    ...value,
    dropdown: false,
    option: null
  })
  const toggleDropdown = () => {
    if (value.dropdown) {
      focusInput()
    }
    onChange({
      ...value,
      dropdown: value.option ? true : !value.dropdown,
      text: '',
      option: null,
      operator: DEFAULT_OPERATOR
    })
  }
  const onCancel = () => {
    focusInput()
    onChange({})
  }
  let cancelLabel = 'Cancel'
  if (value.option === 'camera' && activeQuery.camera.length !== 0) {
    cancelLabel = 'Done'
  }
  if (value.option === 'rating') {
    cancelLabel = 'Done'
  }
  if (value.option === 'library') {
    cancelLabel = 'Done'
  }
  return (
    <div className={disabled ? classNames.wrapperDisabled : classNames.wrapper}>
      <form className={classNames.text} action='' onSubmit={e => e.preventDefault()}>
        <input
          className={'mousetrap ' + (value.dropdown ? classNames.textInputDisabled : classNames.textInput)}
          type='search'
          value={value.text || ''}
          onChange={onChangeText}
          onClick={onClickText}
          autoFocus={autoFocus}
          placeholder={getPlaceholder(value)}
          autoComplete='off'
          autoCorrect='off'
          autoCapitalize='off'
        />
        <Icon icon='search' size={18} className={classNames.searchIcon} />
        <div className={classNames.inputTools}>
          <button className={classNames.inputButton} onClick={toggleDropdown}>
            <Icon icon={value.dropdown ? 'chevron-up' : 'chevron-down'} size={18} />
          </button>
          <button
            className={classNames.cancelButton}
            onClick={onCancel}
            disabled={!value.text && !value.dropdown && !value.option}>
            {cancelLabel}
          </button>
        </div>
      </form>
      {!value.dropdown && value.text &&
        <AutoComplete
          text={value.text.trim()}
          operator={value.operator}
          options={options}
          exclude={activeQuery.keyword.map(k => k.value)}
          addTerm={addTerm}
          focusInput={focusInput}
          onClose={onCancel}
        />
      }
      {value.dropdown &&
        <Dropdown
          value={value.option}
          onChange={option => {
            if (option === null) clearEditingState()
            onChange({ ...value, option })
          }}
          addTerm={addTerm}
          updateTerm={updateTerm}
          updateRating={updateRating}
          removeTerm={removeTerm}
          query={query}
          activeQuery={activeQuery}
          libraries={libraries}
          options={options}
          userHasPerm={userHasPerm}
          dynlinks={dynlinks}
          addNewQuery={addNewQuery && (() => {
            addNewQuery()
            onCancel()
          })}
          setRootProperty={setRootProperty}
          updateTargetDate={updateTargetDate}
        />
      }
    </div>
  )
}

const ADVANCED_TYPES = [
  { type: 'rating', name: 'Rating' },
  { type: 'date', name: 'Timeframe', perm: 'advanced_search' },
  { type: 'location', name: 'Location', perm: 'advanced_search' },
  { type: 'camera', name: 'Camera' },
  { type: 'library', name: 'Libraries' },
  { type: 'dynlink', name: 'Dynamic Link', perm: 'dynamic_links' },
  { type: 'other', name: 'Other' },
  { type: 'limit', name: 'Limit' }
]

const Dropdown = ({ value, onChange, addTerm, updateTerm, updateRating, removeTerm, query, activeQuery, addNewQuery, libraries, options, userHasPerm, dynlinks, setRootProperty, updateTargetDate }) => (
  <div className={classNames.dropdown}>
    {!value &&
      <div className={classNames.dropdownButtons}>
        {ADVANCED_TYPES.map(({ type, name, perm }) => {
          if (type === 'library' && libraries.length === 1) {
            return null
          }
          if (perm && userHasPerm(perm) === false) {
            return null
          }
          return (
            <button key={type} className={classNames.dropdownButton} onClick={() => onChange(type)}>
              {name}
            </button>
          )
        })}
        <button className={classNames.addQueryButton} onClick={addNewQuery} disabled={!addNewQuery}>
          Add query
        </button>
      </div>
    }
    {value === 'rating' &&
      <Field
        component={RatingPicker}
        name='rating'
        props={{
          updateRating,
          onCancel: () => onChange(null)
        }}
        defaultValue={activeQuery.rating}
      />
    }
    {value === 'date' &&
      <Field
        component={DatePicker}
        name='date'
        props={{
          addTerm: (type, value) => updateTerm({
            type,
            value: { ...value, operator: DEFAULT_OPERATOR },
            termIndex: activeQuery.date.length
          }),
          onCancel: () => onChange(null),
          showDynamic: userHasPerm('advanced_search'),
          options,
          targetDate: query.structure.date_target,
          updateTargetDate
        }}
        defaultValue={{}}
      />
    }
    {value === 'location' &&
      <Field
        component={AreaPicker}
        name='location'
        props={{
          updateTerm,
          onCancel: () => onChange(null),
          style: { padding: '10px' }
        }}
        defaultValue={activeQuery.location[0] || {
          lat: 51.509,
          lon: -0.197,
          radius: 25
        }}
      />
    }
    {value === 'camera' &&
      <Field
        component={CameraPicker}
        name='camera'
        props={{
          addTerm,
          removeTerm,
          cameras: options.cameras,
          selected: activeQuery.camera
        }}
      />
    }
    {value === 'library' &&
      <Field
        component={LibraryPicker}
        name='library'
        props={{
          addTerm,
          removeTerm,
          libraries,
          onCancel: () => onChange(null)
        }}
        defaultValue={activeQuery.library}
      />
    }
    {value === 'limit' &&
      <Field
        component={LimitForm}
        name='limit'
        props={{
          setRootProperty,
          onCancel: () => onChange(null)
        }}
        defaultValue={{
          limit: query.structure.limit || '',
          diversity: query.structure.diversity || 0,
          seed: query.structure.seed || ''
        }}
      />
    }
    {value === 'other' &&
      <Field
        component={AdvancedForm}
        name='other'
        props={{
          updateTerm,
          onCancel: () => onChange(null)
        }}
        defaultValue={activeQuery.other[0] || {
          filename: '',
          transmissionReference: ''
        }}
      />
    }
    {value === 'dynlink' &&
      <Field
        component={DynamicLinkForm}
        name='dynlink'
        props={{
          addTerm: (type, value) => updateTerm({
            type,
            value: { ...value, operator: DEFAULT_OPERATOR },
            termIndex: activeQuery.dynlink.length
          }),
          dynlinks,
          onCancel: () => onChange(null)
        }}
      />
    }
  </div>
)

function getPlaceholder ({ dropdown, option }) {
  if (!dropdown) {
    return 'Find images'
  }
  if (!option) {
    return 'Select an advanced search type'
  }
  if (option === 'rating') {
    return 'Select a star rating'
  }
  if (option === 'camera') {
    return 'Select a camera'
  }
  if (option === 'date') {
    return 'Select a timeframe'
  }
  if (option === 'library') {
    return 'Toggle to filter by library'
  }
  if (option === 'location') {
    return 'Search by location'
  }
  if (option === 'dynlink') {
    return 'Create/edit dynamic link'
  }
  if (option === 'limit') {
    return 'Limit the number of images'
  }
  if (option === 'other') {
    return 'Other search'
  }
  return ''
}



// WEBPACK FOOTER //
// ./src/components/search/ImageSearchInput.js